import { Links, SEOConfig } from '@betterfunnel/types';
import Tabs from '@betterfunnel/ui-core/Tabs';
import { updateProfile } from 'firebase/auth';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountCustomCodes } from '../../components/CustomCodes/AccountCustomCodes';
import { SEOForm } from '../../components/settings/SEOForm';
import SettingSectionTextArea from '../../components/settings/SettingSectionTextArea';
import SettingSectionTextField from '../../components/settings/SettingSectionTextField';
import SettingSectionTitle from '../../components/settings/SettingSectionTitle';
import SettingSectionToggle from '../../components/settings/SettingSectionToggle';
import { useAuditLog } from '../../contexts/AuditLogContext';
import { AuthContext } from '../../contexts/AuthContext';
import { DashboardContext } from '../../contexts/DashboardContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import { createAccountAccessToken } from '../../services/AccountsService';

export default function GeneralSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { show, hide } = useContext(SnackbarContext);
  const { firebaseUser, isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { accountConfig, updateAccountConfig } = useContext(DashboardContext);
  const auditLog = useAuditLog();

  const [name, setName] = useState('');
  const [websiteLink, setWebsiteLink] = useState<string>('');
  const [headCustomCode, setHeadCustomCode] = useState<string>('');
  const [headCustomCodeDelay, setHeadCustomCodeDelay] = useState<number>(0);
  const [bodyCustomCode, setBodyCustomCode] = useState<string>('');
  const [bodyCustomCodeDelay, setBodyCustomCodeDelay] = useState<number>(0);
  const [termsOfUseLink, setTermsOfUseLink] = useState<string>('');
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string>('');
  const [urlSuffix, setUrlSuffix] = useState<string>('');
  const [disableAutoAddParameters, setDisableAutoAddParameters] =
    useState<boolean>();
  const [links, setLinks] = useState<Links>({});

  const [selectedSubTab, setSelectedSubTab] = useState<string>(
    searchParams.get('tab') || 'profile',
  );

  useEffect(() => {
    setSearchParams((prev) => {
      prev.set('tab', selectedSubTab);
      return new URLSearchParams(prev);
    });
  }, [selectedSubTab, setSearchParams]);

  useEffect(() => {
    if (!accountConfig?.websiteLink) return;

    setWebsiteLink(accountConfig.websiteLink);
  }, [accountConfig?.websiteLink]);

  useEffect(() => {
    if (!accountConfig?.urlSuffix) return;

    setUrlSuffix(accountConfig.urlSuffix);
  }, [accountConfig?.urlSuffix]);

  useEffect(() => {
    if (accountConfig?.disableAutoAddParameters === undefined) return;

    setDisableAutoAddParameters(accountConfig.disableAutoAddParameters);
  }, [accountConfig?.disableAutoAddParameters]);

  useEffect(() => {
    if (!accountConfig?.privacyPolicyLink) return;

    setPrivacyPolicyLink(accountConfig.privacyPolicyLink);
  }, [accountConfig?.privacyPolicyLink]);

  useEffect(() => {
    if (!accountConfig?.termsOfUseLink) return;

    setTermsOfUseLink(accountConfig.termsOfUseLink);
  }, [accountConfig?.termsOfUseLink]);

  useEffect(() => {
    if (!accountConfig?.links) return;

    setLinks(accountConfig.links);
  }, [accountConfig?.links]);

  useEffect(() => {
    if (!accountConfig?.headCustomCode) return;

    setHeadCustomCode(accountConfig.headCustomCode);
  }, [accountConfig?.headCustomCode]);

  useEffect(() => {
    if (!accountConfig?.headCustomCodeDelay) return;

    setHeadCustomCodeDelay(accountConfig.headCustomCodeDelay);
  }, [accountConfig?.headCustomCodeDelay]);

  useEffect(() => {
    if (!accountConfig?.bodyCustomCode) return;

    setBodyCustomCode(accountConfig.bodyCustomCode);
  }, [accountConfig?.bodyCustomCode]);

  useEffect(() => {
    if (!accountConfig?.bodyCustomCodeDelay) return;

    setBodyCustomCodeDelay(accountConfig.bodyCustomCodeDelay);
  }, [accountConfig?.bodyCustomCodeDelay]);

  useEffect(() => {
    setName(firebaseUser?.displayName || '');
  }, [firebaseUser]);

  const updateName = useCallback(async () => {
    if (!firebaseUser) return;
    if (firebaseUser.displayName === name) return;

    try {
      await updateProfile(firebaseUser, { displayName: name });
      show('Updated');
    } catch (_) {
      show('Failed to update');
    }
  }, [firebaseUser, name, show]);

  const updateSEO = useCallback(
    async (seo: Partial<SEOConfig>) => {
      updateAccountConfig({
        seo: seo,
      });
    },
    [updateAccountConfig],
  );

  const subTabs = useMemo(
    () =>
      [
        {
          id: 'profile',
          name: 'Profile',
          current: false,
        },
        {
          id: 'links',
          name: 'Links',
          current: false,
        },
        {
          id: 'tracking',
          name: 'Tracking & URL Settings',
          current: false,
        },
        {
          id: 'custom-code',
          name: 'Custom Code',
          current: false,
        },
        {
          id: 'seo',
          name: 'SEO',
          current: false,
        },
      ].map((c) => {
        c.current = c.id === selectedSubTab;
        return c;
      }),
    [selectedSubTab],
  );

  return (
    <div>
      <div className="sticky top-0 w-full bg-white">
        <Tabs
          className=""
          tabs={subTabs}
          onTabClick={(id) => setSelectedSubTab(id)}
        />
      </div>

      <div className="max-w-2xl py-8">
        <div className="">
          <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
            {selectedSubTab === 'profile' ? (
              <div>
                <SettingSectionTitle
                  title="Profile"
                  description="This information will be displayed publicly so be careful what you
              share."
                />

                <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
                  <SettingSectionTextField
                    title="Name"
                    value={name}
                    setValue={setName}
                    update={updateName}
                    editable={true}
                  />
                  <div className="pt-6 sm:flex">
                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Email address
                    </dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <div className="text-gray-900">{firebaseUser?.email}</div>
                    </dd>
                  </div>
                </dl>
              </div>
            ) : (
              <></>
            )}

            {selectedSubTab === 'links' ? (
              <div>
                <SettingSectionTitle
                  title="General links"
                  description="Links that will be used in all of your landing pages"
                />

                <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
                  <SettingSectionTextField
                    title="Logo link"
                    value={websiteLink}
                    setValue={setWebsiteLink}
                    update={() => {
                      updateAccountConfig({ websiteLink });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="Terms of use"
                    value={termsOfUseLink}
                    setValue={setTermsOfUseLink}
                    update={() => {
                      updateAccountConfig({ termsOfUseLink });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="Privacy policy"
                    value={privacyPolicyLink}
                    setValue={setPrivacyPolicyLink}
                    update={() => {
                      updateAccountConfig({ privacyPolicyLink });
                    }}
                    editable={true}
                  />
                </dl>
              </div>
            ) : (
              <></>
            )}

            {selectedSubTab === 'tracking' ? (
              <div>
                <SettingSectionTitle
                  title="Tracking & URL Settings"
                  description="Control how your landing pages handle URL parameters and tracking info."
                />

                <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
                  <SettingSectionTextField
                    title="Global Url Suffix"
                    value={urlSuffix}
                    setValue={setUrlSuffix}
                    update={() => {
                      updateAccountConfig({ urlSuffix });
                    }}
                    editable={true}
                    helpText="Add a fixed suffix to all links on your pages"
                    placeholder="param1=value1&param2=value2"
                    pattern="^([a-zA-Z0-9]+=(?:[a-zA-Z0-9]+|\{\{[a-zA-Z0-9]+\}\}))(?:&[a-zA-Z0-9]+=(?:[a-zA-Z0-9]+|\{\{[a-zA-Z0-9]+\}\}))*$"
                  />
                  <SettingSectionToggle
                    title="Add Parameters Automatically"
                    value={!disableAutoAddParameters}
                    setValue={(v: boolean) => setDisableAutoAddParameters(!v)}
                    update={() => {
                      updateAccountConfig({
                        disableAutoAddParameters,
                      });
                    }}
                    editable={true}
                    helpText="If enabled, all url parameters will be added automatically to the links on your pages. If disabled, you will need to add them manually."
                  />
                </dl>
              </div>
            ) : (
              <></>
            )}

            {selectedSubTab === 'custom-code' ? (
              <div>
                <SettingSectionTitle
                  title="Custom Code"
                  description="Add custom code to all of your landing pages."
                />

                <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
                  <SettingSectionTextArea
                    title="Head"
                    value={headCustomCode}
                    setValue={setHeadCustomCode}
                    update={() => {
                      updateAccountConfig({ headCustomCode });
                    }}
                    editable={true}
                  />
                  {isSupportAdmin && (
                    <SettingSectionTextField
                      title="Head Delay"
                      value={headCustomCodeDelay + ''}
                      setValue={(value: any) => {
                        setHeadCustomCodeDelay(
                          Number.isNaN(+value) ? 0 : +value,
                        );
                      }}
                      update={() => {
                        updateAccountConfig({ headCustomCodeDelay });
                      }}
                      pattern="^\d*$"
                      editable
                    />
                  )}
                  <SettingSectionTextArea
                    title="Body"
                    value={bodyCustomCode}
                    setValue={setBodyCustomCode}
                    update={() => {
                      updateAccountConfig({ bodyCustomCode });
                    }}
                    editable={true}
                  />
                  {isSupportAdmin && (
                    <SettingSectionTextField
                      title="Body Delay"
                      value={bodyCustomCodeDelay + ''}
                      setValue={(value: any) => {
                        setBodyCustomCodeDelay(
                          Number.isNaN(+value) ? 0 : +value,
                        );
                      }}
                      update={() => {
                        updateAccountConfig({ bodyCustomCodeDelay });
                      }}
                      pattern="^\d*$"
                      editable
                    />
                  )}
                </dl>

                {/* New Multi-Code Management */}
                <div className="mt-12">
                  <AccountCustomCodes />
                </div>
              </div>
            ) : (
              <></>
            )}

            {selectedSubTab === 'seo' ? (
              <div>
                <SettingSectionTitle
                  title="SEO"
                  description="Edit global SEO to all of your landing pages."
                />

                <dl>
                  <SEOForm
                    seo={accountConfig?.seo ?? {}}
                    onChange={updateSEO}
                  />
                </dl>
              </div>
            ) : (
              <></>
            )}

            {selectedSubTab === 'links' ? (
              <div>
                <SettingSectionTitle
                  title="Links"
                  description="Terms of use, privacy policay and social links to be use in different areas"
                />

                <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
                  <SettingSectionTextField
                    title="Instagram"
                    value={links['instagram'] || ''}
                    setValue={(v: string) =>
                      setLinks({ ...links, instagram: v })
                    }
                    update={() => {
                      updateAccountConfig({
                        links,
                      });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="Facebook"
                    value={links['facebook'] || ''}
                    setValue={(v: string) =>
                      setLinks({ ...links, facebook: v })
                    }
                    update={() => {
                      updateAccountConfig({
                        links,
                      });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="GitHub"
                    value={links['github'] || ''}
                    setValue={(v: string) => setLinks({ ...links, github: v })}
                    update={() => {
                      updateAccountConfig({
                        links,
                      });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="TikTok"
                    value={links['tiktok'] || ''}
                    setValue={(v: string) => setLinks({ ...links, tiktok: v })}
                    update={() => {
                      updateAccountConfig({
                        links,
                      });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="YouTube"
                    value={links['youtube'] || ''}
                    setValue={(v: string) => setLinks({ ...links, youtube: v })}
                    update={() => {
                      updateAccountConfig({
                        links,
                      });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="X (Twitter)"
                    value={links['x'] || ''}
                    setValue={(v: string) => setLinks({ ...links, x: v })}
                    update={() => {
                      updateAccountConfig({
                        links,
                      });
                    }}
                    editable={true}
                  />
                  <SettingSectionTextField
                    title="LinkedIn"
                    value={links['linkedin'] || ''}
                    setValue={(v: string) =>
                      setLinks({ ...links, linkedin: v })
                    }
                    update={() => {
                      updateAccountConfig({
                        links,
                      });
                    }}
                    editable={true}
                  />
                </dl>
              </div>
            ) : (
              <></>
            )}

            {isSupportAdmin && selectedSubTab === 'profile' ? (
              <div>
                <SettingSectionTitle
                  title="Admin"
                  description="This information will be useful for admins only."
                />

                <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
                  <div className="pt-6 sm:flex">
                    <dt
                      className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6"
                      onDoubleClick={() => {
                        if (!selectedAccount) return;
                        const snId = show(
                          'Creating Account Token...',
                          'loading',
                          { disableAutoClose: true },
                        );
                        auditLog.log(
                          'create',
                          'Create Account Token',
                          'account_access_token',
                          selectedAccount.id,
                          'Account Access Token',
                          undefined,
                          undefined,
                        );
                        createAccountAccessToken(selectedAccount.id)
                          .then((token) => {
                            hide(snId.id);
                            navigator.clipboard.writeText(token);
                            show(
                              'Account Token Created and Copied to Clipboard',
                              'success',
                            );
                          })
                          .catch((error) => {
                            console.error(
                              'Error Creating Account Token',
                              error,
                            );
                            hide(snId.id);
                            show('Error Creating Account Token', 'warn');
                          });
                      }}
                    >
                      Account Id
                    </dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <div className="text-gray-900">{selectedAccount?.id}</div>
                    </dd>
                  </div>
                </dl>
              </div>
            ) : (
              <></>
            )}

            {/* <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Bank accounts
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Connect bank accounts to your account.
            </p>

            <ul
              role="list"
              className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6"
            >
              <li className="flex justify-between gap-x-6 py-6">
                <div className="font-medium text-gray-900">TD Canada Trust</div>
                <button
                  type="button"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Update
                </button>
              </li>
              <li className="flex justify-between gap-x-6 py-6">
                <div className="font-medium text-gray-900">
                  Royal Bank of Canada
                </div>
                <button
                  type="button"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Update
                </button>
              </li>
            </ul>

            <div className="flex border-t border-gray-100 pt-6">
              <button
                type="button"
                className="text-sm font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
              >
                <span aria-hidden="true">+</span> Add another bank
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Integrations
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Connect applications to your account.
            </p>

            <ul
              role="list"
              className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6"
            >
              <li className="flex justify-between gap-x-6 py-6">
                <div className="font-medium text-gray-900">QuickBooks</div>
                <button
                  type="button"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Update
                </button>
              </li>
            </ul>

            <div className="flex border-t border-gray-100 pt-6">
              <button
                type="button"
                className="text-sm font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
              >
                <span aria-hidden="true">+</span> Add another application
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Language and dates
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Choose what language and date format to use throughout your
              account.
            </p>

            <dl className="mt-6 [&>*]:pb-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Language
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">English</div>
                  <button
                    type="button"
                    className="font-semibold text-indigo-600 hover:text-indigo-500"
                  >
                    Update
                  </button>
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Date format
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">DD-MM-YYYY</div>
                  <button
                    type="button"
                    className="font-semibold text-indigo-600 hover:text-indigo-500"
                  >
                    Update
                  </button>
                </dd>
              </div>
              <Switch.Group as="div" className="flex pt-6">
                <Switch.Label
                  as="dt"
                  className="flex-none pr-6 font-medium text-gray-900 sm:w-64"
                  passive
                >
                  Automatic timezone
                </Switch.Label>
                <dd className="flex flex-auto items-center justify-end">
                  <Switch
                    checked={automaticTimezoneEnabled}
                    onChange={setAutomaticTimezoneEnabled}
                    className={classNames(
                      automaticTimezoneEnabled
                        ? 'bg-indigo-600'
                        : 'bg-gray-200',
                      'flex w-8 cursor-pointer rounded-full p-px ring-1 ring-inset ring-gray-900/5 transition-colors duration-200 ease-in-out focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        automaticTimezoneEnabled
                          ? 'translate-x-3.5'
                          : 'translate-x-0',
                        'h-4 w-4 transform rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition duration-200 ease-in-out',
                      )}
                    />
                  </Switch>
                </dd>
              </Switch.Group>
            </dl>
          </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}
